import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ModalContainer from '@components/modals/ModalContainer';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchDepartments, fetchEnumValues } from '@services/commonService';
import {
	findAllCategories,
	saveTrendResearchUserPreference,
	updateTrendResearchUserPreference
} from '@services/trendResearch';

import { capitalizeFirstLetter } from '@utils/Util';

const PreferenceModal = ({ onClose, preferences, callback }) => {
	const [markets, setMarkets] = useState([]);
	const [categories, setCategories] = useState({});
	const [fabricTypes, setFabricTypes] = useState([]);
	const [activeTabCount, setActiveTabCount] = useState(1);
	const [fabricTypeList, setFabricTypeList] = useState([]);
	const [categoryIdList, setCategoryIdList] = useState([]);
	const [productGroupIdList, setProductGroupIdList] = useState([]);
	const dispatch = useDispatch();

	useEffect(() => {
		if (preferences !== null) {
			setFabricTypeList(preferences.fabricTypeList?.map((item) => item.constant));
			setCategoryIdList(preferences.categoryResponseList?.map((item) => item.id));
			setProductGroupIdList(preferences.productGroupResponseList?.map((item) => item.id));
		}
	}, [preferences]);

	const preferenceTabs = [
		{ label: 'Market', value: 1 },
		{ label: 'Fabric Type', value: 2 },
		{ label: 'Category', value: 3 }
	];

	const fetchMarkets = async () => {
		try {
			const data = await fetchDepartments();
			setMarkets(data);
		} catch {
			/* empty */
		}
	};

	const fetchCategories = async () => {
		try {
			const data = await findAllCategories();
			const groupedData = data.reduce((acc, item) => {
				const key = item.parentCategory.constant;
				if (!acc[key]) {
					acc[key] = [];
				}
				acc[key].push(item);
				return acc;
			}, {});
			setCategories(groupedData);
		} catch {
			/* empty */
		}
	};

	const fetchFabricTypes = async () => {
		try {
			const data = await fetchEnumValues('info.nitex.app.materials.FabricType');
			setFabricTypes(data?.map((item) => ({ id: item.constant, name: item.name })));
		} catch {
			/* empty */
		}
	};

	useEffect(() => {
		(async () => {
			await Promise.all([fetchMarkets(), fetchFabricTypes(), fetchCategories()]);
		})();
	}, []);

	const handlePrevious = () => {
		setActiveTabCount(activeTabCount - 1);
	};

	const handleNext = () => {
		setActiveTabCount(activeTabCount + 1);
	};

	const renderOptions = (data, type) => {
		const typeMap = {
			fabricTypeList: [fabricTypeList, setFabricTypeList],
			productGroupIdList: [productGroupIdList, setProductGroupIdList],
			categoryIdList: [categoryIdList, setCategoryIdList]
		};

		const [list, setList] = typeMap[type] || [];

		const toggleDataSelection = (id) => {
			if (!list || !setList) return;
			if (list.includes(id)) {
				setList(list.filter((item) => item !== id));
			} else {
				setList((prev) => [...prev, id]);
			}
		};

		return (
			<div className="categories-item">
				{data
					?.sort((a, b) => a.name.localeCompare(b.name))
					?.map((item) => (
						<div
							className={`category-single cursor-pointer ${
								list.includes(item.id) ? 'active' : ''
							}`}
							key={`${type}_${item.id}`}
							onClick={() => toggleDataSelection(item.id)}
						>
							<p className="medium-14 gray_dark_01">{item.name}</p>
						</div>
					))}
			</div>
		);
	};

	const handleSubmit = async () => {
		try {
			dispatch(openLoader());
			if (preferences === null) {
				await saveTrendResearchUserPreference({
					fabricTypeList,
					productGroupIdList,
					categoryIdList
				});
			} else {
				await updateTrendResearchUserPreference({
					id: preferences.id,
					fabricTypeList,
					productGroupIdList,
					categoryIdList
				});
			}
			await callback();
			dispatch(closeLoader());
			onClose();
		} catch {
			dispatch(closeLoader());
		}
	};

	return (
		<ModalContainer className="preference-modal">
			<div className="common-popup preference-modal">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte">
						<h3 className="semibold-16 mb-0">Choose your preferences</h3>
					</div>
					<div className="close-btn" onClick={onClose}>
						<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
					</div>
				</div>
				<div className="common-popup-body">
					<div className="type-and-count d-flex justify-content-between">
						<div className="inner-tabs">
							<ul>
								{preferenceTabs.map((item) => (
									<li
										className={activeTabCount === item.value ? 'active' : ''}
										onClick={() => setActiveTabCount(item.value)}
										key={item.label}
									>
										{item.label}
									</li>
								))}
							</ul>
						</div>
						{preferences === null && (
							<div>
								<span className="status dark-1">
									{activeTabCount}/{preferenceTabs.length}
								</span>
							</div>
						)}
					</div>
					{activeTabCount !== 3 && (
						<div className="categories">
							{activeTabCount === 3 && <p className="semibold-14 gray_dark_01 mb-12">Top</p>}
							{renderOptions(
								activeTabCount === 2 ? fabricTypes : markets,
								activeTabCount === 2 ? 'fabricTypeList' : 'productGroupIdList'
							)}
						</div>
					)}
					{activeTabCount === 3 && (
						<div className="categories">
							{Object.keys(categories).map((item) => {
								return (
									<React.Fragment key={item}>
										<p className="semibold-14 gray_dark_01 mb-12 title">
											{capitalizeFirstLetter(item.replace('_', ' '))}
										</p>
										{renderOptions(categories[item], 'categoryIdList')}
									</React.Fragment>
								);
							})}
						</div>
					)}
				</div>
				<div className="modal-footer">
					<div className="footer-buttons text-right">
						{activeTabCount > 1 && (
							<button className="button size32 outline" onClick={handlePrevious}>
								Previous
							</button>
						)}
						{activeTabCount < 3 && (
							<button className="button size32 ml-2" onClick={handleNext}>
								Next
							</button>
						)}
						{activeTabCount === 3 && (
							<button
								type="submit"
								className="button size32 ml-2"
								disabled={
									fabricTypeList.length === 0 &&
									categoryIdList.length === 0 &&
									productGroupIdList.length === 0
								}
								onClick={handleSubmit}
							>
								Submit
							</button>
						)}
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default PreferenceModal;
